import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  useGetAllCustomerListQuery,
  useGetCustomerGroupCountQuery,
} from "../../../../store/api/customer";
import ActionButtonDropdown from "../../../../Components/ActionButtonDropdown";
import Pagination from "../../../../Core/Pagination/Pagination";
import ListLayout from "../../../../Core/Layout/ListLayout";
import Table from "../../../../Core/Table/Table";
import Tabs from "../../../../Components/Tabs";
import ExportCSV from "../../../../Core/Export/ExportCSV";
import ConfigureColumnsNew from "../../../../Core/ConfigureColumnsNew/ConfigureColumnsNew";
import ListFilters from "../../../../Core/ListFilters/ListFilters";
import DelConfirmPopup from "../../../../Components/Popup/DelConfirmPopup ";
import { useDeleteDocMutation } from "../../../../store/api/globalapi";
import useScreenHeight from "../../../../utils/useScreenHeight";

const MainContent = () => {
  const navigate = useNavigate();
  const { screenHeight, screenScaling, pageSize } = useScreenHeight();

  const [filters, setFilters] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [customerData, setCustomerData] = useState([]);
  const [showExportPopup, setShowExportPopup] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showColumnConfig, setShowColumnConfig] = useState(false);

  const [deleteDoc] = useDeleteDocMutation();
  const [delPopupVisible, setDelPopupVisible] = useState(false);
  const [delSelectedId, setDelSelectedId] = useState(null);
  const [delmessage, setDelmessage] = useState();
  const [alreadySubmitData, setAlreadySubmitData] = useState(false);
  const [delDone, setDelDone] = useState(false);

  const doctype = "Customer";

  // Build dynamic filter array
  const buildFilterArray = (filtersObj) => {
    return Object.entries(filtersObj)
      .filter(([key, value]) => key !== "siNo" && value?.trim() !== "")
      .map(([key, value]) => {
        if (key === "creation") return ["Customer", key, "like", `${value}%`];
        if (["name", "customer_group", "territory"].includes(key))
          return ["Customer", key, "like", `%${value}%`];
        if (key === "disabled") return ["Customer", key, "=", value];
        return ["Customer", key, "=", value];
      });
  };

  const siNoFilter = filters.siNo?.trim();
  const isSiNoActive = siNoFilter !== undefined && siNoFilter !== "";
  const targetPage = isSiNoActive
    ? Math.ceil(parseInt(siNoFilter, 10) / pageSize)
    : currentPage;

  // Query: Get customer list
  const { data, isLoading, refetch: refetchCustomerData } =
    useGetAllCustomerListQuery({
      page: targetPage,
      page_size: pageSize,
      search_input: buildFilterArray(filters),
    });

  // Query: Get total customer count
  const { data: totalCount, refetch: refetchCustomerCount } =
    useGetCustomerGroupCountQuery();

  // Set total record count
  useEffect(() => {
    if (totalCount) setTotalRecords(totalCount?.message);
  }, [totalCount]);

  // Handle customer data with serial numbers
 useEffect(() => {
  if (data?.data && Array.isArray(data.data)) {
    const rawData = data.data;
    const numberedData = rawData.map((item, idx) => ({
      ...item,
      siNo: idx + 1 + pageSize * (targetPage - 1),
    }));
    const finalData = isSiNoActive
      ? numberedData.filter((item) => item.siNo === parseInt(siNoFilter, 10))
      : numberedData;

    setCustomerData(finalData);
    if (isSiNoActive && currentPage !== targetPage) {
      setCurrentPage(targetPage);
    }
  }
}, [data, isSiNoActive, siNoFilter, targetPage, currentPage, pageSize]);

  useEffect(() => {
    if (pageSize > 0) refetchCustomerData();
  }, [filters, currentPage, pageSize, screenScaling, screenHeight]);

  const filtersConfig = [
    { name: "siNo", label: "S. No.", width: "5%", type: "number" },
    { name: "name", label: "Customer Name", width: "20%", type: "text" },
    { name: "customer_group", label: "Customer Group", width: "20%", type: "text" },
    { name: "territory", label: "Territory", width: "20%", type: "text" },
    { name: "creation", label: "Date", width: "15%", type: "date" },
    {
      name: "disabled",
      label: "Status",
      width: "10%",
      type: "select",
      options: [
        { value: "0", label: "Enabled" },
        { value: "1", label: "Disabled" },
      ],
    },
  ];

  // CRUD Handlers
  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const handleView = (id) => {
    navigate(`/master/customer/view-customer/`, { state: { PurchaseId: id } });
  };

  const handleEdit = (id) => {
    navigate(`/master/customer/edit-customer/`, { state: { PurchaseId: id } });
  };

  const handleDelete = (id) => {
    setDelSelectedId(id);
    setDelPopupVisible(true);
    setAlreadySubmitData(false);
    setDelmessage(`Are you sure you want to Delete this ${doctype}?`);
  };

  const delHandleCancel = () => {
    setDelPopupVisible(false);
    setDelSelectedId(null);
  };

  const delHandleConfirm = async () => {
    try {
      await deleteDoc({ doctype, name: delSelectedId }).unwrap();
      setDelmessage(`${doctype} ${delSelectedId} deleted successfully!`);
      setDelDone(true);
      setTimeout(() => {
        setDelPopupVisible(false);
        setDelSelectedId(null);
        setDelDone(false);
        refetchCustomerData();
        refetchCustomerCount();
      }, 1000);
    } catch (err) {
      console.error("Deletion Failed:", err);
      let errorMessage = `Failed to delete the ${doctype}!`;
      if (err?.data?._server_messages) {
        try {
          const msgArr = JSON.parse(err.data._server_messages);
          if (msgArr?.length > 0) {
            const msgObj = JSON.parse(msgArr[0]);
            if (msgObj?.message) {
              errorMessage = msgObj.message.replace(/<[^>]+>/g, "");
            }
          }
        } catch {}
      }
      setDelPopupVisible(true);
      setDelmessage(errorMessage);
      setAlreadySubmitData(true);
    }
  };

  // Columns setup
  const [columnsConfig, setColumnsConfig] = useState(() => [
    {
      header: "S. No.",
      accessor: "siNo",
      name: "S. No.",
      visible: true,
      sortable: false,
    },
    { header: "Customer Name", accessor: "name", name: "Customer Name", visible: true },
    { header: "Customer Group", accessor: "customer_group", name: "Customer Group", visible: true },
    { header: "Territory", accessor: "territory", name: "Territory", visible: true },
    {
      header: "Date",
      accessor: "creation",
      name: "Date",
      visible: true,
      cellRenderer: (row) => row.creation?.split(" ")[0],
    },
    {
      header: "Status",
      accessor: "disabled",
      name: "Status",
      visible: true,
      cellRenderer: (row) => {
        const isDisabled = row.disabled;
        const statusText = isDisabled === 0 ? "Enabled" : "Disabled";
        const color = isDisabled === 0 ? "#16A34A" : "#EF4444";
        return <span style={{ color }}>{statusText}</span>;
      },
    },
    {
      header: "Action",
      accessor: "action",
      name: "Action",
      visible: true,
      cellRenderer: (row) => (
        <ActionButtonDropdown
          id={row.name}
          onView={() => handleView(row.name)}
          onEdit={() => handleEdit(row.name)}
          onDelete={() => handleDelete(row.name)}
        />
      ),
    },
  ]);

  const handleSaveColumns = (selectedKeys) => {
    const updated = columnsConfig.map((col) => ({
      ...col,
      visible: selectedKeys.includes(col.accessor),
    }));
    setColumnsConfig(updated);
  };

  const visibleColumns = useMemo(
    () => columnsConfig.filter((col) => col.visible),
    [columnsConfig]
  );

  const defaultSelectedColumns = useMemo(
    () =>
      columnsConfig
        .filter((col) => col.visible)
        .map((col) => ({ fieldname: col.accessor, label: col.name })),
    [columnsConfig]
  );

  const activeFilterCount = useMemo(
    () => Object.values(filters).filter((v) => v !== "" && v != null).length,
    [filters]
  );

  const actionButtons = [
    {
      className: "createbtnsfilter border-0 d-flex",
      style: { gap: "2px", height: "28px" },
      iconName: "IoFilterSharp",
      iconSize: 12,
      children: activeFilterCount > 0 ? `Filter (${activeFilterCount})` : "Filter",
      onClick: () => {
        setShowFilters((prev) => !prev);
        setShowColumnConfig(false);
      },
    },
    {
      className: "createbtnshowmore border-0 d-flex",
      style: { gap: "2px", height: "28px" },
      iconName: "MdOutlineRemoveRedEye",
      iconSize: 12,
      children: "Show more",
      onClick: () => {
        setShowColumnConfig((prev) => !prev);
        setShowFilters(false);
      },
    },
    {
      className: "createbtnexport border-0 d-flex",
      style: { gap: "2px", height: "28px",backgroundColor : '#E6A744' },
      iconName: "RiFileExcel2Fill",
      iconSize: 12,
      children: "Export Excel",
      onClick: () => setShowExportPopup(true),
    },
    {
      className: "custom-btn-00989 d-flex",
      to: "/master/customer/create-customer",
      iconName: "FiPlus",
      style: { gap: "2px", height: "28px" },
      children: "Add Customer",
    },
  ];

  const accessors = columnsConfig
    .filter((col) => col.accessor !== "siNo" && col.accessor !== "action")
    .map((col) => col.accessor);

  return (
    <div className="container-fluid d-flex flex-column" style={{ flexGrow: "1" }}>
      <Tabs />
      <ListLayout
        breadcrumbItems={[
          { label: "Customer" },
          { label: "Customer List", style: { color: "#4b002b" } },
        ]}
        actionButtons={actionButtons}
      >
        {showFilters && (
          <ListFilters
            filtersConfig={filtersConfig}
            onFilterChange={handleFilterChange}
            currentFilters={filters}
            setShowFilters={setShowFilters}
          />
        )}

        {showColumnConfig && (
          <ConfigureColumnsNew
            show={showColumnConfig}
            onClose={() => setShowColumnConfig(false)}
            onSave={handleSaveColumns}
            docType="Customer"
            defaultSelected={defaultSelectedColumns}
            fieldsData={columnsConfig.map((col) => ({
              fieldname: col.accessor,
              label: col.name,
            }))}
            disabledFields={["siNo", "action", "name", "customer_group"]}
          />
        )}

        <Table columns={visibleColumns} data={customerData} loading={isLoading} />

        <Pagination
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          totalRecords={totalRecords}
          currentPage={currentPage}
          className="mt-2 pb-3"
        />

        {delPopupVisible && (
          <DelConfirmPopup
            alredysubmitdata={alreadySubmitData}
            delSelectedId={delSelectedId}
            visible={delPopupVisible}
            message={delmessage}
            onConfirm={delHandleConfirm}
            onCancel={delHandleCancel}
            delDone={delDone}
          />
        )}

        {showExportPopup && (
          <ExportCSV
            doctype="Customer"
            fieldsData={accessors}
            onClose={() => setShowExportPopup(false)}
          />
        )}
      </ListLayout>
    </div>
  );
};

export default MainContent;
